// src/components/UmatUpdateMulti.js
import React, { useEffect, useState } from "react";
import LookupUmat from "./LookupUmat";
import DropDownArea from "./DropDownArea";
import { apiFetch } from "../utils/wkCommon";
import { getUser } from "../utils/auth";
import PERMISSIONS from "../utils/wkCommon";
import { hasUmatPermissionAsync } from "../utils/permissionsAsync";

/* ===== konfigurasi kategori (tetap) ===== */
const CATEGORIES = [
  { key: "area", label: "Area", fields: ["area", "district"] },
  { key: "orangtua", label: "Orangtua", fields: ["father_umat_item", "mother_umat_item"] },
  { key: "usiaindah", label: "Usia Indah", fields: ["ciqing"] },
  { key: "ciqing", label: "Ci Qing", fields: ["smp_SMA"] },
  { key: "chengzhang", label: "Cheng Zhang", fields: ["smp_SMA"] },
  { key: "ertong", label: "Er Tong", fields: ["erTongBan"] },
  { key: "jiangshi", label: "Jiang Shi", fields: ["jiang_Shi", "jsDesc", "jabatanSuci"] },
  { key: "jiangyuan", label: "Jiang Yuan", fields: ["jiangYuan", "jyDesc", "jabatanSuci"] },
  { key: "tanzhu", label: "Tan Zhu", fields: ["tanzhu", "tzDesc", "jabatanSuci"] },
  { key: "qingkou", label: "Qing Kou", fields: ["qingKou", "qkDesc"] },
  { key: "sd3hari", label: "SD 3 Hari", fields: ["sd3Hari", "sd3HDesc", "jabatanSuci"] },
  { key: "mingde", label: "Ming De", fields: ["mingde", "mingdeDesc"] },
  { key: "xinmin", label: "Xin Min", fields: ["xinmin", "xinminDesc"] },
  { key: "zhishan", label: "Zhi Shan", fields: ["zhishan", "zhishanDesc"] },
  { key: "xuande", label: "Xuan De", fields: ["xuande", "xuandeDesc"] },
  { key: "hongde", label: "Hong De", fields: ["hongde", "hongdeDesc"] },
];

const JABATAN_SUKU_MAP = {
  jiangshi: "講師",
  jiangyuan: "講員",
  tanzhu: "壇主",
  sd3hari: "辦事"
};

/* LABELS */
const FIELD_LABELS = {
  area: "Area / Wilayah",
  district: "Distrik",
  ciqing: "Ci Qing",
  smp_SMA: "SMP / SMA",
  erTongBan: "Kelas Anak",
  tanzhu: "Tan Zhu",
  tzDesc: "Keterangan Tan Zhu",
  sd3Hari: "SD 3 Hari",
  sd3HDesc: "Keterangan SD 3 Hari",
  jabatanSuci: "Jabatan Suci",
  jiang_Shi: "Jiang Shi (flag)",
  jsDesc: "Keterangan Jiang Shi",
  jiangYuan: "Jiang Yuan (flag)",
  jyDesc: "Keterangan Jiang Yuan",
  qingKou: "Qing Kou (flag)",
  qkDesc: "Keterangan Qing Kou",
  mingde: "Ming De (flag)",
  mingdeDesc: "Keterangan Ming De",
  xinmin: "Xin Min (flag)",
  xinminDesc: "Keterangan Xin Min",
  zhishan: "Zhi Shan (flag)",
  zhishanDesc: "Keterangan Zhi Shan",
  xuande: "Xuan De (flag)",
  xuandeDesc: "Keterangan Xuan De",
  hongde: "Hong De (flag)",
  hongdeDesc: "Keterangan Hong De",
  father_umat_item: "Orangtua (Ayah) - pilih dari data umat",
  mother_umat_item: "Orangtua (Ibu) - pilih dari data umat",
};

function prettifyFieldName(field) {
  if (!field) return "";
  const s = field.replace(/([A-Z])/g, " $1").replace(/[_\-]+/g, " ");
  return s.replace(/\b\w/g, ch => ch.toUpperCase()).trim();
}

const isDescField = (f) => /desc$/i.test(f) || /Desc$/i.test(f);
const booleanFieldsGuess = new Set([
  "tanzhu", "sd3Hari", "smp_SMA", "ciqing", "mingde", "xinmin",
  "zhishan", "xuande", "hongde", "qingKou", "jiang_Shi", "jiangYuan", "erTongBan"
]);
const isBooleanField = (f) => booleanFieldsGuess.has(f);

/* ========== Styling: inline <style> injected into component ========== 
   - label column fixed width (adjust LABEL_COL_WIDTH)
   - responsive: on narrow screens switch to single-column
   - ensure dropdowns not clipped by overflow
*/
const LABEL_COL_WIDTH = 200; // px, atur jika perlu
const styleString = `
.umum-wrap { overflow: visible; }
.category-wrap { max-width: 520px; width: 100%; overflow: visible; }
.category-select { width: 100%; box-sizing: border-box; padding: 8px; border-radius: 6px; border: 1px solid rgba(0,0,0,0.12); background: #fff; }
.fields-grid { display: grid; gap: 12px; }
.field-row { display: grid; grid-template-columns: ${LABEL_COL_WIDTH}px 1fr; gap: 12px; align-items: center; }
.field-row.align-top { align-items: start; }
.field-label { font-weight: 600; padding-top: 4px; }
.field-control input[type="text"], .field-control select, .field-control textarea { width: 100%; box-sizing: border-box; padding: 8px; border-radius: 6px; border: 1px solid rgba(0,0,0,0.08); font-size: 1rem; }
.field-control textarea { min-height: 96px; resize: vertical; }
.field-boolean { display:flex; align-items:center; gap:8px; }
.note-small { font-size: 15px; color: #444; margin-top: 6px; }
@media (max-width: 720px) {
  .field-row { grid-template-columns: 1fr; }
  .field-label { padding-top: 0; }
}
`;

/* ================= component ================= */
export default function UmatUpdateMulti({ onSaved }) {
  const [selectedUmats, setSelectedUmats] = useState([]);
  const [category, setCategory] = useState("");
  const [values, setValues] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [canEdit, setCanEdit] = useState(false);
  const [canEditAndApply, setCanEditAndApply] = useState(false);

  const user = getUser();

  useEffect(() => {
    let mounted = true;
    async function loadPerms() {
      try {
        const email = user?.email;
        if (!email) {
          if (mounted) { setCanEdit(false); setCanEditAndApply(false); }
          return;
        }
        const pEdit = await hasUmatPermissionAsync(email, PERMISSIONS.UMATDATA_EDIT);
        const pEditApproval = await hasUmatPermissionAsync(email, PERMISSIONS.UMATDATA_EDITAPPROVAL);
        if (mounted) {
          setCanEdit(Boolean(pEdit));
          setCanEditAndApply(Boolean(pEditApproval));
        }
      } catch (err) {
        console.warn("loadPerms error", err);
        if (mounted) { setCanEdit(false); setCanEditAndApply(false); }
      }
    }
    loadPerms();
    return () => { mounted = false; };
  }, [user]);

  useEffect(() => {
    const defaults = {};
    if (JABATAN_SUKU_MAP[category]) {
      defaults.jabatanSuci = JABATAN_SUKU_MAP[category];
    }
    setValues(defaults);
    setMessage(null);
    setError(null);
  }, [category]);

  function handleLookupChange(items) {
    setSelectedUmats(Array.isArray(items) ? items : []);
  }

  function setFieldValue(field, v) {
    setValues(prev => ({ ...prev, [field]: v }));
  }

  /* Render a single field as label + control in two-column layout */
  function renderFieldRow(field) {
    const label = FIELD_LABELS[field] ?? prettifyFieldName(field);
    const needAlignTop = isDescField(field);
    const rowClass = needAlignTop ? "field-row align-top" : "field-row";

    // control node
    let control = null;
    if (field === "area") {
      control = <DropDownArea value={values.area ?? ""} onChange={(id) => setFieldValue("area", id)} />;
    } else if (field === "district") {
      control = <input type="text" value={values.district ?? ""} onChange={(e)=>setFieldValue("district", e.target.value)} />;
    } else if (isDescField(field)) {
      control = <input type="text" placeholder="Tanggal (kelulusan/peresmian/ikrar) dalam format yyyy-mm-dd, sertakan nama tempat jika perlu." value={values[field] ?? ""} onChange={(e)=>setFieldValue(field, e.target.value)} />;
    } else if (field === "jabatanSuci") {
      control = <input type="text" value={values[field] ?? ""} onChange={(e)=>setFieldValue(field, e.target.value)} />;
    } else if (field === "father_umat_item" || field === "mother_umat_item") {
      control = (
        <LookupUmat
          value={values[field] || null}
          onChange={(it) => setFieldValue(field, it)}
          multiple={false}
          includePassedAway={true}
        />
      );
    } else if (isBooleanField(field)) {
      const TRISTATE_OPTIONS = [
        { label: "無紀錄 Tidak ada catatan", value: "null" },
        { label: "未 Belum", value: "false" },
        { label: "已 Sudah", value: "true" },
      ];

      const currentVal =
        values[field] === null || values[field] === undefined
          ? "null"
          : values[field] === true
          ? "true"
          : "false";

      control = (
        <select
          value={currentVal}
          onChange={(e) => {
            const v = e.target.value;
            let parsed = null;
            if (v === "true") parsed = true;
            else if (v === "false") parsed = false;
            else parsed = null;

            setFieldValue(field, parsed);
          }}
        >
          {TRISTATE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      );
    } else {
      control = <input type="text" value={values[field] ?? ""} onChange={(e)=>setFieldValue(field, e.target.value)} />;
    }

    return (
      <div key={field} className={rowClass}>
        <div className="field-label">{label}</div>
        <div className="field-control">{control}</div>
      </div>
    );
  }

  function renderCategoryFields() {
    if (!category) return null;
    const cfg = CATEGORIES.find(c => c.key === category);
    if (!cfg) return null;
    return (
      <div className="fields-grid">
        {cfg.fields.map(f => renderFieldRow(f))}
        {JABATAN_SUKU_MAP[category] && (
          <div className="note-small">
            <em>Note: {FIELD_LABELS["jabatanSuci"] ?? prettifyFieldName("jabatanSuci")} diisi default “{JABATAN_SUKU_MAP[category]}” untuk kategori ini, dapat diubah.</em>
          </div>
        )}
      </div>
    );
  }

  function buildPayload() {
    if (!category) return {};
    const cfg = CATEGORIES.find(c => c.key === category);
    if (!cfg) return {};
    const payload = {};
    cfg.fields.forEach(f => {
      // ✅ HANDLE LOOKUP UMAT
      if (f === "father_umat_item" || f === "mother_umat_item") {
        const it = values[f];

        let id = null;
        if (it) {
          // samakan dengan individual (backward compatible)
          id = it.id ?? it.documentId ?? null;
        }

        payload[f] = id;
        return;
      }

      if (isBooleanField(f)) {
        if (values[f] === null) payload[f] = null;
        else if (values[f] === true) payload[f] = true;
        else if (values[f] === false) payload[f] = false;
      }
      else if (isDescField(f)) payload[f] = values[f] ?? "";
      else payload[f] = values[f] ?? null;
    });
    return payload;
  }

  async function createUmatUpdateRecord(opts = {}) {
    const userIdOrName = user?.username ?? null;
    const body = {
      data: {
        type: "bulk",
        updateStatus: opts.updateStatus ?? "Pending",
        targetUmats: opts.targetUmats || [],
        updatePayload: opts.payload || {},
        appliedStatus: opts.appliedStatus ?? null,
        originalValues: opts.originalValues ?? null,
        notes: opts.notes ?? null,
        recordOwner: userIdOrName,
        recordEditor: userIdOrName,
        recordApprover: opts.recordApprover ?? null
      }
    };
    return await apiFetch("/api/umat-updates", { method: "POST", body });
  }

  async function handleSubmit(e) {
    e && e.preventDefault();
    setError(null);
    setMessage(null);

    if ((!canEdit) && (!canEditAndApply)) {
      setError("Anda tidak memiliki izin untuk submit perubahan.");
      return;
    }
    if (!category) { setError("Pilih kategori perubahan terlebih dahulu."); return; }
    if (!selectedUmats || selectedUmats.length === 0) { setError("Pilih minimal satu umat (LookupUmat)."); return; }

    const payload = buildPayload();
    if (!payload || Object.keys(payload).length === 0) { setError("Tidak ada nilai valid untuk disimpan."); return; }

    const targetIds = selectedUmats.map(u => u.documentId ?? null).filter(Boolean);
    if (targetIds.length === 0) { setError("Selected umat tidak memiliki id yang valid."); return; }

    setSubmitting(true);
    try {
      if (canEditAndApply) {
        const applyPromises = targetIds.map(async (docId) => {
          try {
            const resp = await apiFetch(`/api/umats/${docId}`, {
              method: "PUT",
              body: { data: payload }
            });

            if (!resp || resp.error) {
              throw new Error("Invalid response");
            }

            return { id: docId, success: true };

          } catch (err) {
            return {
              id: docId,
              success: false,
              error: err?.message ?? String(err)
            };
          }
        });
        //const results = await Promise.allSettled(applyPromises);
        //const appliedStatus = results.map(r => (r.status === "fulfilled" ? r.value : { id: null, success: false, error: r.reason?.message ?? String(r.reason) }));
        const appliedStatus = await Promise.all(applyPromises);
        const anySuccess = appliedStatus.some(x => x.success === true);
        const createResp = await createUmatUpdateRecord({
          updateStatus: anySuccess ? "Applied" : "Pending",
          targetUmats: targetIds,
          payload,
          appliedStatus,
          notes: anySuccess ? "Applied by approver (bulk)" : "Apply attempted but failed for all targets",
        });
        if (anySuccess) setMessage({ type: "success", text: "Perubahan diterapkan ke beberapa umat dan tersimpan di umat-updates." });
        else setMessage({ type: "error", text: "Gagal menerapkan perubahan ke semua umat. Permintaan tersimpan untuk ditinjau." });
        onSaved && onSaved(createResp);
      } else if (canEdit) {
        const createResp = await createUmatUpdateRecord({
          updateStatus: "Pending",
          targetUmats: targetIds,
          payload,
          notes: "Bulk update request (pending approval)"
        });
        setMessage({ type: "success", text: "Permintaan perubahan tersimpan (menunggu approval)." });
        onSaved && onSaved(createResp);
      } else {
        setError("Tidak ada permission untuk submit.");
      }
    } catch (err) {
      console.error("Submit bulk error:", err);
      setError(err?.message ?? String(err));
      setMessage({ type: "error", text: "Gagal menyimpan perubahan: " + (err?.message ?? String(err)) });
    } finally {
      setSubmitting(false);
    }
  }

  const userIdOrName = user?.username ?? "unknown";

  return (
    <div className="umum-wrap" style={{ fontSize: "0.95rem" }}>
      {/* inject style */}
      <style>{styleString}</style>

      <div style={{ marginBottom: 12 }}>
        <label style={{ display: "block", marginBottom: 6, fontWeight: 700, color: "#2b4a84" }}>Pilih Umat (multiple)</label>
        <LookupUmat value={selectedUmats} onChange={handleLookupChange} multiple={true} />
        <div style={{ fontSize: 12, color: "#6b7280", marginTop: 6 }}>{selectedUmats.length} umat terpilih</div>
      </div>

      {message && <div style={{ marginBottom: 12, padding: 8, borderRadius: 8, background: message.type === "success" ? "#d1e7dd" : "#f8d7da", color: message.type === "success" ? "#0f5132" : "#842029" }}>{message.text}</div>}
      {error && <div style={{ color: "crimson", marginBottom: 8 }}>{error}</div>}
      {!canEdit && !canEditAndApply && <div style={{ marginBottom: 12, padding: 8, background: "#fff7e6", borderRadius: 6, color: "#666" }}>Anda tidak punya izin untuk submit perubahan.</div>}

      <form onSubmit={handleSubmit}>
        <div style={{ display: "grid", gap: 10 }}>
          <div className="category-wrap" style={{ marginBottom: 8 }}>
            <label style={{ fontWeight: 700, display: "block", marginBottom: 8 }}>Kategori perubahan</label>
            {/* make select full width + not clipped */}
            <select
              className="category-select"
              value={category}
              onChange={(e)=>setCategory(e.target.value)}
            >
              <option value="">-- pilih kategori --</option>
              {CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </div>

          <div className="fields-grid">
            {renderCategoryFields()}
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
            <div style={{ alignSelf: "center", fontSize: 13, color: "#555", display: "none" }}>Editor: {userIdOrName}</div>
            <button type="submit" disabled={submitting || (!canEdit && !canEditAndApply)} style={{
              background: "#2b4a84",
              color: "#fff",
              border: "none",
              padding: "8px 14px",
              borderRadius: 6,
              cursor: submitting ? "wait" : "pointer",
              fontWeight: 600
            }}>{submitting ? "Submitting…" : (canEditAndApply ? "Apply & Save" : "Submit")}</button>
          </div>
        </div>
      </form>
    </div>
  );
}
